"use client";

import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import {
  Button,
  Center,
  H3,
  List,
  Modal,
  Paper,
  Stack,
  Text,
} from "@/components/ui/elements";
import {
  AttentionIcon,
  CheckIcon,
  TimeFiveIcon,
} from "@/components/ui/elements/icon";

/** LoginRestrictionStatus: restricted | request_pending | lifted */
export type LoginRestrictionStatus =
  | "restricted"
  | "request_pending"
  | "lifted";

type LoginRestrictionModalProps = {
  opened: boolean;
  onClose: () => void;
  /** 制限理由（APIから取得した場合に表示） */
  reason?: string | null;
  /** 制限ステータス（表示内容を切り替え） */
  status?: LoginRestrictionStatus | null;
  /** 制限解除申請ボタン押下時に呼ばれる（別ダイアログを開く場合はここで閉じてから開く） */
  onRequestReactivation?: () => void;
};

const STATUS_CONFIG: Record<
  NonNullable<LoginRestrictionStatus>,
  {
    title: string;
    description: string;
    footer: string;
    icon: React.ReactNode;
    color: string;
    bgColor: string;
    borderColor: string;
  }
> = {
  restricted: {
    title: "ログイン制限中です",
    description:
      "入力された電話番号がログイン制限されています。解除をご希望の場合は、制限解除申請を入力して送信してください。",
    footer:
      "身に覚えがない等の場合は、その旨を制限解除申請フォームに記載して送信してください。",
    icon: <AttentionIcon size="32px" color="var(--mantine-color-red-6)" />,
    color: "red.9",
    bgColor: "var(--mantine-color-red-0)",
    borderColor: "var(--mantine-color-red-2)",
  },
  request_pending: {
    title: "制限解除申請中です",
    description:
      "制限解除申請を承っております。審査完了まで今しばらくお待ちください。審査には数日〜1週間程度お時間をいただく場合がございます。",
    footer:
      "ご不明な点がございましたら、フリーダイヤル、問合せメールアドレスよりお気軽にご連絡ください。",
    icon: <TimeFiveIcon size="32px" color="var(--mantine-color-orange-6)" />,
    color: "orange.9",
    bgColor: "var(--mantine-color-orange-0)",
    borderColor: "var(--mantine-color-orange-2)",
  },
  lifted: {
    title: "制限解除済み",
    description:
      "ログイン制限は解除されました。現在は正常にログインいただけます。",
    footer:
      "ご不明な点がございましたら、お問い合わせフォームよりお気軽にご連絡ください。",
    icon: <CheckIcon size="32px" color="var(--mantine-color-green-6)" />,
    color: "green.9",
    bgColor: "var(--mantine-color-green-0)",
    borderColor: "var(--mantine-color-green-2)",
  },
};

export function LoginRestrictionModal({
  opened,
  onClose,
  reason,
  status = "restricted",
  onRequestReactivation,
}: LoginRestrictionModalProps) {
  const router = useRouter();

  /** 閉じる押下時: モーダルを閉じ、サインアップ画面へ遷移（全画面共通） */
  const handleClose = () => {
    onClose();
    if (
      typeof window !== "undefined" &&
      window.opener &&
      !window.opener.closed
    ) {
      const baseUrl = window.location.origin;
      window.opener.location.href = `${baseUrl}/signup`;
      window.close();
    } else {
      router.push("/signup");
    }
  };
  const [displayStatus, setDisplayStatus] = useState<LoginRestrictionStatus>(
    () => (status ?? "restricted") as LoginRestrictionStatus,
  );

  useEffect(() => {
    if (opened) {
      setDisplayStatus((status ?? "restricted") as LoginRestrictionStatus);
    }
  }, [opened, status]);

  const effectiveStatus = displayStatus;
  const config = STATUS_CONFIG[effectiveStatus] ?? STATUS_CONFIG.restricted;

  /** restricted の場合のみ制限解除申請ボタンを表示（request_pending / lifted では非表示） */
  const canSubmitReactivationRequest = effectiveStatus === "restricted";

  const handleRequestReactivationClick = () => {
    onClose();
    onRequestReactivation?.();
  };

  return (
    <Modal
      opened={opened}
      close={handleClose}
      centered
      size="md"
      padding="sm"
      withCloseButton={false}
      radius="lg"
    >
      <Stack gap="lg" pb="lg">
        <Stack gap="md" align="center">
          <Center
            style={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              backgroundColor: config.bgColor,
              border: `1px solid ${config.borderColor}`,
            }}
          >
            {config.icon}
          </Center>
          <H3 ta="center" c={config.color}>
            {config.title}
          </H3>
          <Text size="sm" ta="center" style={{ lineHeight: 1.6 }}>
            {config.description}
          </Text>
        </Stack>

        {effectiveStatus === "restricted" && (
          <Paper withBorder p="md" radius="md" bg="gray.0">
            <Text size="xs" fw={700} c="dimmed" mb={4}>
              制限の理由
            </Text>
            <Text size="xs" c="dimmed" mb="xs">
              以下のような理由により、現在アカウントが制限されている可能性がございます。
            </Text>
            <List size="sm" spacing="xs">
              <List.Item>以前ケアリンクを利用していて退会をした場合</List.Item>
              <List.Item>重大な利用規約違反があった場合</List.Item>
            </List>
          </Paper>
        )}

        <Stack gap="xs">
          {canSubmitReactivationRequest && (
            <Button
              color="blue"
              onClick={handleRequestReactivationClick}
              variant="filled"
              fullWidth
              size="md"
            >
              制限解除申請を行う
            </Button>
          )}
          <Button
            color="gray"
            onClick={handleClose}
            variant="light"
            fullWidth
            size="md"
          >
            閉じる
          </Button>
        </Stack>

        <Text ta="center" c="dimmed" size="xs">
          {config.footer}
        </Text>
      </Stack>
    </Modal>
  );
}
